import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Download, ExternalLink, ArrowLeft, CheckCircle2, Wrench } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/word-addin")({
  head: () => ({
    meta: [
      { title: "LexAIPro per Microsoft Word — Installazione Add-in" },
      { name: "description", content: "Installa LexAIPro come add-in di Microsoft Word per ricerca, analisi e redazione legale assistita direttamente nel tuo documento." },
      { property: "og:title", content: "LexAIPro per Microsoft Word" },
      { property: "og:description", content: "Add-in ufficiale per Word desktop e Word per il web." },
    ],
  }),
  component: WordAddinPage,
});



function WordAddinPage() {
  const [wipOpen, setWipOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-3xl px-6 py-12 sm:py-20">
        <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Torna al sito
        </Link>

        <div className="mb-2 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <span className="flex h-5 w-5 items-center justify-center rounded bg-[#185ABD] text-[10px] font-bold text-white">W</span>
          Microsoft Word Add-in
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
          Usa LexAIPro direttamente in Word
        </h1>
        <p className="mt-3 max-w-xl text-base text-muted-foreground">
          Ricerca normativa, analisi e redazione assistita senza uscire dal documento. Funziona con Word per il web, Word desktop (Windows / macOS) e Word per iPad.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setWipOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-foreground px-5 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-90"
          >
            <Download className="h-4 w-4" /> Scarica il manifest
          </button>
          <button
            type="button"
            onClick={() => setWipOpen(true)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
          >
            <ExternalLink className="h-4 w-4" /> Apri Word per il web
          </button>
        </div>

        <Dialog open={wipOpen} onOpenChange={setWipOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                <Wrench className="h-6 w-6" />
              </div>
              <DialogTitle className="text-center">Work in progress</DialogTitle>
              <DialogDescription className="text-center">
                L'add-in di LexAIPro per Microsoft Word è in fase di sviluppo. Ti avviseremo via email non appena sarà disponibile per il download.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="sm:justify-center">
              <Button onClick={() => setWipOpen(false)}>Ho capito</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ol className="mt-12 space-y-6">
          <Step n={1} title="Scarica il file manifest">
            Clicca su "Scarica il manifest" qui sopra. Il file <code className="rounded bg-muted px-1.5 py-0.5 text-xs">lexaipro-manifest.xml</code> verrà salvato nella cartella Download.
          </Step>
          <Step n={2} title="Sideload in Word">
            <p className="mb-2"><strong>Word per il web:</strong> apri un documento → menu <em>Inserisci</em> → <em>Componenti aggiuntivi</em> → <em>Carica componente aggiuntivo personale</em> → seleziona il manifest.</p>
            <p><strong>Word desktop:</strong> menu <em>Inserisci</em> → <em>Componenti aggiuntivi</em> → <em>I miei componenti aggiuntivi</em> → <em>Carica un mio componente aggiuntivo</em> → seleziona il manifest.</p>
          </Step>
          <Step n={3} title="Accedi e inizia">
            Nel pannello LexAIPro che appare a destra, accedi con le credenziali del tuo account. Il pannello resta agganciato al documento e funziona offline-friendly.
          </Step>
        </ol>

        <div className="mt-12 rounded-xl border border-border bg-card p-5">
          <h3 className="font-display text-lg font-semibold">Hai bisogno di aiuto?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Se l'opzione "Carica componente personale" non compare, contatta l'IT del tuo studio: in molte aziende il sideload va abilitato dall'amministratore Microsoft 365.
          </p>
          <a href="mailto:beleadcreativestudio@proton.me" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium underline-offset-4 hover:underline">
            Scrivi a beleadcreativestudio@proton.me <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground text-sm font-semibold text-background">
        {n}
      </div>
      <div className="flex-1 pt-1">
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {title}
        </h3>
        <div className="mt-1 text-sm leading-relaxed text-muted-foreground">{children}</div>
      </div>
    </li>
  );
}
